const PAGE_DEBUG_LOGS_KEY = '__pageDebugLogs';
const PAGE_DEBUG_DOWNLOAD_KEY = 'downloadPageDebugLog';
const PAGE_DEBUG_CLEAR_KEY = 'clearPageDebugLog';
const PAGE_DEBUG_COPY_KEY = 'copyPageDebugLog';
const PAGE_DEBUG_MAX_COUNT = 300;
const PAGE_DEBUG_MAX_STRING_LENGTH = 2000;
const PAGE_DEBUG_BUTTON_ID = '__pageDebugLogButton';
const PAGE_DEBUG_ACTIONS_ID = '__pageDebugLogActions';

let pageDebugLogs = [];
let isButtonInited = false;

/**
 * 获取当前浏览器 window 对象，兼容非浏览器环境。
 * @returns {Window|null} 浏览器 window 对象
 */
function getDebugWindow() {
  // 非浏览器环境没有 window，日志工具只返回空对象避免影响主流程。
  if (typeof window === 'undefined') {
    return null;
  }
  return window;
}

/**
 * 输出日志工具自身告警，避免工具异常中断业务。
 * @param {Error} error - 日志工具捕获到的异常
 * @returns {void}
 */
function warnPageDebugLogError(error) {
  // console 不存在时直接跳过，避免低版本环境二次异常。
  if (typeof console === 'undefined' || !console.warn) {
    return;
  }
  console.warn('[pageDebugLog]', error);
}

/**
 * 判断字符串是否需要转换为摘要。
 * @param {string} value - 待检查字符串
 * @returns {boolean} 需要摘要时返回 true
 */
function shouldSummarizeString(value) {
  // dataURL/base64/blob URL 不完整入库，避免日志体积过大。
  if (
    /^data:[^;]+;base64,/.test(value) ||
    /^blob:/.test(value) ||
    /^[A-Za-z0-9+/]{200,}={0,2}$/.test(value)
  ) {
    return true;
  }
  // 超长字符串也只记录摘要。
  if (value.length > PAGE_DEBUG_MAX_STRING_LENGTH) {
    return true;
  }
  return false;
}

/**
 * 生成字符串摘要，保留排查需要的前后片段和长度。
 * @param {string} value - 原始字符串
 * @returns {Object|string} 摘要对象或原始字符串
 */
function summarizeString(value) {
  // 短字符串直接原样记录。
  if (!shouldSummarizeString(value)) {
    return value;
  }
  return {
    __pageDebugSummary: true,
    type: resolveStringSummaryType(value),
    length: value.length,
    start: value.slice(0, 120),
    end: value.slice(-80)
  };
}

/**
 * 获取字符串摘要类型。
 * @param {string} value - 原始字符串
 * @returns {string} 摘要类型
 */
function resolveStringSummaryType(value) {
  // data URL 单独标识，方便排查图片或文件来源。
  if (/^data:[^;]+;base64,/.test(value)) {
    return 'dataUrl';
  }
  // blob URL 单独标识，方便排查浏览器临时资源。
  if (/^blob:/.test(value)) {
    return 'blobUrl';
  }
  // 疑似裸 base64 单独标识，避免误以为是普通长文本。
  if (/^[A-Za-z0-9+/]{200,}={0,2}$/.test(value)) {
    return 'base64';
  }
  return 'longString';
}

/**
 * 递归生成可下载日志快照，避免循环引用和大字段。
 * @param {*} value - 原始日志值
 * @param {Array} seenList - 已访问对象列表
 * @returns {*} 可安全序列化的日志值
 */
function sanitizeLogValue(value, seenList) {
  // 字符串统一检查是否需要摘要。
  if (typeof value === 'string') {
    return summarizeString(value);
  }
  // 基础类型直接记录。
  if (!value || typeof value !== 'object') {
    return value;
  }
  // Error 只保留关键错误信息。
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack ? value.stack.slice(0, 1000) : ''
    };
  }
  // 循环引用转为标记，保证 JSON.stringify 不报错。
  if (seenList.indexOf(value) > -1) {
    return '[Circular]';
  }

  const nextSeenList = seenList.concat([value]);
  // 数组逐项清洗。
  if (Array.isArray(value)) {
    return value.map(function (item) {
      return sanitizeLogValue(item, nextSeenList);
    });
  }

  const output = {};
  Object.keys(value).forEach(function (key) {
    output[key] = sanitizeLogValue(value[key], nextSeenList);
  });
  return output;
}

/**
 * 获取当前日志数组，并同步到 window 上方便控制台查看。
 * @returns {Array} 当前页面日志数组
 */
function getPageDebugLogs() {
  const win = getDebugWindow();
  // window 上存在历史数组时沿用，避免热更新或重复初始化丢日志。
  if (win && Array.isArray(win[PAGE_DEBUG_LOGS_KEY])) {
    pageDebugLogs = win[PAGE_DEBUG_LOGS_KEY];
  }
  // 浏览器环境同步挂到 window，方便控制台复查。
  if (win) {
    win[PAGE_DEBUG_LOGS_KEY] = pageDebugLogs;
  }
  return pageDebugLogs;
}

/**
 * 记录一条页面调试日志。
 * @param {string} title - 日志标题
 * @param {Object} [detail] - 日志详情
 * @param {Object} [options] - 日志选项
 * @param {string} [options.consolePrefix] - 控制台输出前缀
 * @param {boolean} [options.printConsole=true] - 是否同步输出到 console.log
 * @returns {Object|null} 写入的日志项，失败时返回 null
 */
export function recordPageDebugLog(title, detail = {}, options = {}) {
  try {
    const safeDetail = sanitizeLogValue(detail || {}, []);
    const entry = {
      time: new Date().toISOString(),
      title,
      ...safeDetail
    };
    const logs = getPageDebugLogs();
    logs.push(entry);
    // 日志只保留最近固定条数，避免长时间调试后内存过大。
    if (logs.length > PAGE_DEBUG_MAX_COUNT) {
      logs.splice(0, logs.length - PAGE_DEBUG_MAX_COUNT);
    }
    // 默认同步打印到控制台，保持原调试习惯。
    if (options.printConsole !== false && typeof console !== 'undefined') {
      console.log(options.consolePrefix || '[pageDebugLog]', entry);
    }
    return entry;
  } catch (error) {
    warnPageDebugLogError(error);
    return null;
  }
}

/**
 * 下载当前页面调试日志文件。
 * @param {string} [fileName] - 下载文件名
 * @returns {number} 下载的日志条数
 */
export function downloadPageDebugLog(fileName) {
  try {
    const win = getDebugWindow();
    // 非浏览器环境无法触发下载。
    if (!win || !win.document || !win.Blob || !win.URL) {
      return 0;
    }
    const logs = getPageDebugLogs();
    const content = JSON.stringify(logs, null, 2);
    const blob = new Blob([content], {
      type: 'application/json;charset=utf-8'
    });
    const url = win.URL.createObjectURL(blob);
    const link = win.document.createElement('a');
    link.href = url;
    link.download = fileName || `page-debug-log-${Date.now()}.json`;
    win.document.body.appendChild(link);
    link.click();
    win.document.body.removeChild(link);
    win.setTimeout(function () {
      win.URL.revokeObjectURL(url);
    }, 0);
    return logs.length;
  } catch (error) {
    warnPageDebugLogError(error);
    return 0;
  }
}

/**
 * 清空当前页面调试日志。
 * @returns {number} 清空后的日志条数
 */
export function clearPageDebugLog() {
  try {
    pageDebugLogs = [];
    const win = getDebugWindow();
    // 浏览器环境同步清理 window 上的日志数组。
    if (win) {
      win[PAGE_DEBUG_LOGS_KEY] = pageDebugLogs;
    }
    return pageDebugLogs.length;
  } catch (error) {
    warnPageDebugLogError(error);
    return 0;
  }
}

/**
 * 获取压缩后的页面调试日志字符串。
 * @returns {string} 压缩后的日志 JSON 字符串
 */
function getCompactPageDebugLogText() {
  try {
    return JSON.stringify(getPageDebugLogs());
  } catch (error) {
    warnPageDebugLogError(error);
    return '[]';
  }
}

/**
 * 使用 textarea 兜底复制文本。
 * @param {string} text - 待复制文本
 * @returns {boolean} 是否复制成功
 */
function fallbackCopyText(text) {
  try {
    const win = getDebugWindow();
    // 非浏览器环境无法使用 DOM 复制。
    if (!win || !win.document || !win.document.body) {
      return false;
    }
    const textArea = win.document.createElement('textarea');
    textArea.value = text;
    setStyle(
      textArea,
      `
        position: fixed;
        left: -9999px;
        top: -9999px;
        opacity: 0;
      `
    );
    win.document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const success = win.document.execCommand('copy');
    win.document.body.removeChild(textArea);
    return success;
  } catch (error) {
    warnPageDebugLogError(error);
    return false;
  }
}

/**
 * 复制当前页面调试日志。
 * @returns {Promise<boolean>} 是否复制成功
 */
export function copyPageDebugLog() {
  const text = getCompactPageDebugLogText();
  try {
    const win = getDebugWindow();
    // 支持 Clipboard API 时优先使用系统复制接口。
    if (
      win &&
      win.navigator &&
      win.navigator.clipboard &&
      win.navigator.clipboard.writeText
    ) {
      return win.navigator.clipboard
        .writeText(text)
        .then(function () {
          return true;
        })
        .catch(function (error) {
          warnPageDebugLogError(error);
          return fallbackCopyText(text);
        });
    }
    return Promise.resolve(fallbackCopyText(text));
  } catch (error) {
    warnPageDebugLogError(error);
    return Promise.resolve(false);
  }
}

/**
 * 设置元素的样式文本。
 * @param {HTMLElement} element - 目标元素
 * @param {string} styleText - 样式文本
 * @returns {void}
 */
function setStyle(element, styleText) {
  element.style.cssText = styleText;
}

/**
 * 移除已有页面日志控件，确保热更新后按钮结构能重新生成。
 * @returns {void}
 */
function removeExistingDebugControls() {
  const win = getDebugWindow();
  // 非浏览器环境无需处理 DOM。
  if (!win || !win.document) {
    return;
  }
  const oldButton = win.document.getElementById(PAGE_DEBUG_BUTTON_ID);
  const oldActions = win.document.getElementById(PAGE_DEBUG_ACTIONS_ID);
  // 旧日志按钮存在时移除，避免缺少新增按钮或旧事件未更新。
  if (oldButton && oldButton.parentNode) {
    oldButton.parentNode.removeChild(oldButton);
  }
  // 旧操作面板存在时移除，确保下载/复制/清空三个按钮同步生成。
  if (oldActions && oldActions.parentNode) {
    oldActions.parentNode.removeChild(oldActions);
  }
}

/**
 * 读取日志按钮保存的位置。
 * @returns {{top:number,right:number}} 按钮位置
 */
function getSavedButtonPosition() {
  const position = {
    top: 12,
    right: 12
  };
  try {
    const savedPosition = localStorage.getItem('pageDebugLogButtonPosition');
    // 没有保存过位置时使用默认右上角。
    if (!savedPosition) {
      return position;
    }
    const parsedPosition = JSON.parse(savedPosition);
    return {
      top: Number(parsedPosition.top) || position.top,
      right:
        parsedPosition.right === 0 || parsedPosition.right
          ? Number(parsedPosition.right)
          : position.right
    };
  } catch (error) {
    warnPageDebugLogError(error);
    return position;
  }
}

/**
 * 将日志按钮位置限制在当前可视区域内。
 * @param {{top:number,right:number}} position - 原始按钮位置
 * @returns {{top:number,right:number}} 限制后的按钮位置
 */
function clampSavedButtonPosition(position) {
  const maxTop = Math.max(0, window.innerHeight - 32);
  const maxRight = Math.max(0, window.innerWidth - 44);
  const top = Math.min(Math.max(0, position.top), maxTop);
  const right = Math.min(Math.max(0, position.right), maxRight);
  return {
    top,
    right
  };
}

/**
 * 保存日志按钮当前位置。
 * @param {HTMLElement} button - 日志按钮元素
 * @returns {void}
 */
function saveButtonPosition(button) {
  try {
    const style = window.getComputedStyle(button);
    localStorage.setItem(
      'pageDebugLogButtonPosition',
      JSON.stringify({
        top: parseFloat(style.top) || 0,
        right: parseFloat(style.right) || 0
      })
    );
  } catch (error) {
    warnPageDebugLogError(error);
  }
}

/**
 * 根据坐标设置按钮位置，并限制在可视区域内。
 * @param {HTMLElement} button - 日志按钮元素
 * @param {number} left - 目标 left 坐标
 * @param {number} top - 目标 top 坐标
 * @returns {void}
 */
function setButtonPosition(button, left, top) {
  const rect = button.getBoundingClientRect();
  const maxLeft = Math.max(0, window.innerWidth - rect.width);
  const maxTop = Math.max(0, window.innerHeight - rect.height);
  const nextLeft = Math.min(Math.max(0, left), maxLeft);
  const nextTop = Math.min(Math.max(0, top), maxTop);
  button.style.left = '';
  button.style.right = window.innerWidth - nextLeft - rect.width + 'px';
  button.style.top = nextTop + 'px';
}

/**
 * 获取鼠标或触摸事件坐标。
 * @param {MouseEvent|TouchEvent} event - 鼠标或触摸事件
 * @returns {{x:number,y:number}} 事件坐标
 */
function getEventPoint(event) {
  // 触摸事件优先读取第一个触点。
  if (event.touches && event.touches.length) {
    return {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY
    };
  }
  return {
    x: event.clientX,
    y: event.clientY
  };
}

/**
 * 给日志按钮绑定拖动能力。
 * @param {HTMLElement} button - 日志按钮元素
 * @param {Function} onClick - 未拖动时的点击回调
 * @param {Function} [onMove] - 拖动时的移动回调
 * @returns {void}
 */
function bindButtonDrag(button, onClick, onMove) {
  let dragging = false;
  let moved = false;
  let startPoint = null;
  let startRect = null;

  /**
   * 开始拖动日志按钮。
   * @param {MouseEvent|TouchEvent} event - 鼠标或触摸事件
   * @returns {void}
   */
  function handleStart(event) {
    dragging = true;
    moved = false;
    startPoint = getEventPoint(event);
    startRect = button.getBoundingClientRect();
    event.preventDefault();
  }

  /**
   * 移动日志按钮。
   * @param {MouseEvent|TouchEvent} event - 鼠标或触摸事件
   * @returns {void}
   */
  function handleMove(event) {
    // 未进入拖动状态时不处理移动事件。
    if (!dragging || !startPoint || !startRect) {
      return;
    }
    const point = getEventPoint(event);
    const diffX = point.x - startPoint.x;
    const diffY = point.y - startPoint.y;
    // 超过 4px 视为拖动，避免轻微点击抖动误判。
    if (Math.sqrt(diffX * diffX + diffY * diffY) > 4) {
      moved = true;
    }
    setButtonPosition(button, startRect.left + diffX, startRect.top + diffY);
    // 外部需要同步跟随元素时，拖动过程中一并通知。
    if (onMove) {
      onMove();
    }
    event.preventDefault();
  }

  /**
   * 结束拖动或触发点击。
   * @param {MouseEvent|TouchEvent} event - 鼠标或触摸事件
   * @returns {void}
   */
  function handleEnd(event) {
    // 未进入拖动状态时不处理结束事件。
    if (!dragging) {
      return;
    }
    dragging = false;
    // 发生拖动时保存位置，否则作为点击打开操作按钮。
    if (moved) {
      saveButtonPosition(button);
      // 拖动结束后再同步一次，确保位置持久化后仍对齐。
      if (onMove) {
        onMove();
      }
    } else {
      onClick();
    }
    event.preventDefault();
  }

  button.addEventListener('mousedown', handleStart);
  button.addEventListener('touchstart', handleStart, { passive: false });
  document.addEventListener('mousemove', handleMove);
  document.addEventListener('touchmove', handleMove, { passive: false });
  document.addEventListener('mouseup', handleEnd);
  document.addEventListener('touchend', handleEnd);
}

/**
 * 创建日志操作按钮容器。
 * @returns {HTMLElement} 日志操作按钮容器
 */
function createActionsPanel() {
  const actions = document.createElement('div');
  actions.id = PAGE_DEBUG_ACTIONS_ID;
  setStyle(
    actions,
    `
      position: fixed;
      top: 54px;
      right: 12px;
      z-index: 2147483647;
      display: none;
      gap: 6px;
      padding: 6px;
      background: rgba(0, 0, 0, 0.72);
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    `
  );

  const downloadButton = document.createElement('button');
  downloadButton.type = 'button';
  downloadButton.textContent = '下载';
  setStyle(
    downloadButton,
    `
      border: 0;
      border-radius: 3px;
      padding: 6px 10px;
      background: #1989fa;
      color: #fff;
      font-size: 12px;
    `
  );
  downloadButton.onclick = function () {
    downloadPageDebugLog();
  };

  const copyButton = document.createElement('button');
  copyButton.type = 'button';
  copyButton.textContent = '复制';
  setStyle(
    copyButton,
    `
      border: 0;
      border-radius: 3px;
      padding: 6px 10px;
      background: #07c160;
      color: #fff;
      font-size: 12px;
    `
  );
  copyButton.onclick = function () {
    copyPageDebugLog();
  };

  const clearButton = document.createElement('button');
  clearButton.type = 'button';
  clearButton.textContent = '清空';
  setStyle(
    clearButton,
    `
      border: 0;
      border-radius: 3px;
      padding: 6px 10px;
      background: #ee0a24;
      color: #fff;
      font-size: 12px;
    `
  );
  clearButton.onclick = function () {
    clearPageDebugLog();
  };

  actions.appendChild(downloadButton);
  actions.appendChild(copyButton);
  actions.appendChild(clearButton);
  return actions;
}

/**
 * 创建页面右上角日志按钮。
 * @returns {HTMLElement} 日志按钮元素
 */
function createDebugButton() {
  const button = document.createElement('button');
  const position = clampSavedButtonPosition(getSavedButtonPosition());
  button.id = PAGE_DEBUG_BUTTON_ID;
  button.type = 'button';
  button.textContent = '日志';
  setStyle(
    button,
    `
      position: fixed;
      top: ${position.top}px;
      right: ${position.right}px;
      z-index: 2147483647;
      border: 0;
      border-radius: 4px;
      padding: 8px 12px;
      background: #323233;
      color: #fff;
      font-size: 12px;
      line-height: 1;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      cursor: move;
      user-select: none;
    `
  );
  return button;
}

/**
 * 同步日志操作按钮位置到日志按钮附近。
 * @param {HTMLElement} button - 日志按钮元素
 * @param {HTMLElement} actions - 日志操作按钮容器
 * @returns {void}
 */
function syncActionsPosition(button, actions) {
  const rect = button.getBoundingClientRect();
  const actionsRect = actions.getBoundingClientRect();
  const top = Math.min(
    rect.bottom + 6,
    Math.max(0, window.innerHeight - actionsRect.height)
  );
  const right = Math.min(
    Math.max(0, window.innerWidth - rect.right),
    Math.max(0, window.innerWidth - actionsRect.width)
  );
  actions.style.top = top + 'px';
  actions.style.right = right + 'px';
}

/**
 * 初始化页面日志按钮和全局下载/清空入口。
 * @param {boolean} enabled - 是否启用页面日志按钮
 * @returns {void}
 */
export function initPageDebugLogButton(enabled) {
  try {
    const win = getDebugWindow();
    // 非浏览器环境只跳过 DOM 初始化。
    if (!win || !win.document) {
      return;
    }
    win[PAGE_DEBUG_LOGS_KEY] = getPageDebugLogs();
    win[PAGE_DEBUG_DOWNLOAD_KEY] = downloadPageDebugLog;
    win[PAGE_DEBUG_CLEAR_KEY] = clearPageDebugLog;
    win[PAGE_DEBUG_COPY_KEY] = copyPageDebugLog;
    // body 尚未创建时延后初始化按钮，避免入口脚本加载过早。
    if (!win.document.body) {
      win.document.addEventListener(
        'DOMContentLoaded',
        function () {
          initPageDebugLogButton(enabled);
        },
        { once: true }
      );
      return;
    }
    // 非开发模式仅挂载全局方法，不创建页面按钮。
    // 非开发模式仅保留控制台全局方法。
    if (!enabled) {
      return;
    }
    removeExistingDebugControls();
    isButtonInited = true;
    const button = createDebugButton();
    const actions = createActionsPanel();
    bindButtonDrag(
      button,
      function () {
        const isVisible = actions.style.display !== 'none';
        // 打开前先显示，才能读取操作面板真实宽高用于边界约束。
        if (!isVisible) {
          actions.style.display = 'flex';
          syncActionsPosition(button, actions);
          return;
        }
        actions.style.display = 'none';
      },
      function () {
        // 操作面板打开时跟随按钮位置，避免拖动后按钮和面板分离。
        if (actions.style.display !== 'none') {
          syncActionsPosition(button, actions);
        }
      }
    );
    win.document.body.appendChild(button);
    win.document.body.appendChild(actions);
  } catch (error) {
    warnPageDebugLogError(error);
  }
}

export default {
  recordPageDebugLog,
  downloadPageDebugLog,
  clearPageDebugLog,
  copyPageDebugLog,
  initPageDebugLogButton
};
